import {
  StyledNonCovered,
  StyledTable,
  StyledTableCaption,
  StyledTableCell,
  StyledTableRow,
} from "../styled";

/**
 * Stand-in amounts. They are internally consistent (rows add up to the totals)
 * so the mock reads like a real estimate rather than random numbers.
 */
export const previewAmounts = {
  allowed: "$2,400.00",
  insurancePays: "$1,560.00",
  deductible: "$500.00",
  coinsurance: "$340.00",
  patientTotal: "$840.00",
  promptPay: "$756.00",
  percentOff: "10",
  surgeonFees: "$1,200.00",
  anesthesiaFees: "$450.00",
  facilityFees: "$2,100.00",
  exparel: "$180.00",
  scd: "$95.00",
  convenienceFee: "$80.00",
  v3Total: "$4,105.00",
};

/** The default estimate table: insurance figures ending in the discounted price. */
export function DefaultChargeTable() {
  return (
    <StyledTable $columns="1fr 70px">
      <StyledTableRow $tone="head">
        <StyledTableCell>CHARGE DESCRIPTIONS AMOUNT</StyledTableCell>
        <StyledTableCell $right>AMOUNT</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="gray">
        <StyledTableCell>Total insurance allowed amount</StyledTableCell>
        <StyledTableCell $right>{previewAmounts.allowed}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow>
        <StyledTableCell>
          Your insurance will pay for this service
        </StyledTableCell>
        <StyledTableCell $right>{previewAmounts.insurancePays}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="gray">
        <StyledTableCell>
          Your deductible amount for this service
        </StyledTableCell>
        <StyledTableCell $right>{previewAmounts.deductible}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow>
        <StyledTableCell>
          Your co-insurance amount for this service
        </StyledTableCell>
        <StyledTableCell $right>{previewAmounts.coinsurance}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="blue">
        <StyledTableCell>Your total estimated payment due</StyledTableCell>
        <StyledTableCell $right>{previewAmounts.patientTotal}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="navy">
        <StyledTableCell>
          Prompt pay price ({previewAmounts.percentOff}% discount)
        </StyledTableCell>
        <StyledTableCell $right>{previewAmounts.promptPay}</StyledTableCell>
      </StyledTableRow>
    </StyledTable>
  );
}

const breakdownRows = [
  {
    provider: "Facility",
    cptCodes: "45380",
    insuranceRate: "$1,800.00",
    insurancePays: "$1,170.00",
    youPay: "$630.00",
  },
  {
    provider: "Anesthesiologist",
    cptCodes: "00810",
    insuranceRate: "$600.00",
    insurancePays: "$390.00",
    youPay: "$210.00",
  },
];

/** The no-discount table: a per-provider breakdown with no discounted price. */
export function NoDiscountBreakdownTable() {
  return (
    <>
      <StyledTableCaption>Payment Breakdown</StyledTableCaption>

      <StyledTable $columns="1fr 50px 60px 60px 50px">
        <StyledTableRow $tone="head">
          <StyledTableCell>Provider</StyledTableCell>
          <StyledTableCell>CPT Codes</StyledTableCell>
          <StyledTableCell $right>Insurance Rate</StyledTableCell>
          <StyledTableCell $right>Insurance Pays</StyledTableCell>
          <StyledTableCell $right>You Pay</StyledTableCell>
        </StyledTableRow>

        {breakdownRows.map((row) => (
          <StyledTableRow key={row.provider}>
            <StyledTableCell>{row.provider}</StyledTableCell>
            <StyledTableCell>{row.cptCodes}</StyledTableCell>
            <StyledTableCell $right>{row.insuranceRate}</StyledTableCell>
            <StyledTableCell $right>{row.insurancePays}</StyledTableCell>
            <StyledTableCell $right>{row.youPay}</StyledTableCell>
          </StyledTableRow>
        ))}

        <StyledTableRow $tone="head">
          <StyledTableCell>Total</StyledTableCell>
          <StyledTableCell />
          <StyledTableCell $right>{previewAmounts.allowed}</StyledTableCell>
          <StyledTableCell $right>
            {previewAmounts.insurancePays}
          </StyledTableCell>
          <StyledTableCell $right>
            {previewAmounts.patientTotal}
          </StyledTableCell>
        </StyledTableRow>
      </StyledTable>

      <StyledNonCovered>
        <strong>Not covered by insurance</strong>
        <div>
          <span>Specimen handling</span>
          <span>$75.00</span>
        </div>
      </StyledNonCovered>
    </>
  );
}

/** The version 3 table: flat fee categories, no insurance figures. */
export function V3ChargeTable() {
  return (
    <StyledTable $columns="1fr 70px">
      <StyledTableRow $tone="head">
        <StyledTableCell>CHARGE DESCRIPTION</StyledTableCell>
        <StyledTableCell $right>AMOUNT</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="gray">
        <StyledTableCell>Surgeon fees</StyledTableCell>
        <StyledTableCell $right>{previewAmounts.surgeonFees}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow>
        <StyledTableCell>Anesthesia fees</StyledTableCell>
        <StyledTableCell $right>
          {previewAmounts.anesthesiaFees}
        </StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="gray">
        <StyledTableCell>Facility fees</StyledTableCell>
        <StyledTableCell $right>{previewAmounts.facilityFees}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow>
        <StyledTableCell>Exparel</StyledTableCell>
        <StyledTableCell $right>{previewAmounts.exparel}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="blue">
        <StyledTableCell>SCD</StyledTableCell>
        <StyledTableCell $right>{previewAmounts.scd}</StyledTableCell>
      </StyledTableRow>

      <StyledTableRow>
        <StyledTableCell>Convenience and service fees</StyledTableCell>
        <StyledTableCell $right>
          {previewAmounts.convenienceFee}
        </StyledTableCell>
      </StyledTableRow>

      <StyledTableRow $tone="navy">
        <StyledTableCell>Total Responsibility</StyledTableCell>
        <StyledTableCell $right>{previewAmounts.v3Total}</StyledTableCell>
      </StyledTableRow>
    </StyledTable>
  );
}
